/* ── The sign ─────────────────────────────────────────────────────────────
   A neon sign: `THE` in block caps and `Afterparty` in script.

   Two kinds of geometry, because a sign shop uses two.

   `Afterparty` is real type — Sacramento, cut to its eight letters in
   `scripts/fonts.mjs` and set here as SVG `<text>`. It is here because it is
   *monoline*: near enough one width from end to end, which is the one thing a
   glass tube also is. A script with real calligraphic contrast (Mr Dafoe was
   the other candidate, and is the better lettering) would have to be beaten
   flat before it could pass for tubing, and beating a face flat is a thing you
   can see. Sacramento arrives the right shape.

   `THE` is drawn here as bezier centrelines. Block neon caps are bent tube,
   not letterforms.

   That split is why there are two layer recipes below. A centreline has no
   width of its own and the stroke *is* the tube; a glyph already has a body
   and the stroke only swells it. Both end up as the same six layers and both
   answer to `--neon-lit`; see `styles/neon.css`.

   The grid, in the viewBox's own units:

        -34  cap height of THE
         28  its baseline
        300  the script's baseline, at font-size 300
        444  the y's descender

   The script sits on its own baseline at its own angle, a degree and a bit off
   the caps above it, and the whole sign is hung a few degrees off level again.
   Nothing here is centred on anything, which is the point.
   ─────────────────────────────────────────────────────────────────────── */

/**
 * The whole canvas, in user units. Every filter region is pinned to it, which
 * means the padding here is not slack — it is the room the outermost glow
 * needs. A gaussian reaches about three times its deviation, the far glow is
 * blurred by 24, so nothing lit may come within ~70 units of these edges or
 * its halo gets cut off square against nothing.
 */
const VIEW = { x: 13, y: -135, w: 1258, h: 690 } as const;

/**
 * Where the script is set. The initial is deliberately oversized: it is the
 * structural stroke of the sign, with `THE` tucked into its upper shoulder.
 * The rest of the word begins just beyond the initial's final stroke. Their
 * halos meet, but the glass itself does not cross and double up.
 */
const SCRIPT = {
  x: 80,
  y: 330,
  initialSize: 390,
  restSize: 270,
  restOffset: 10,
  restLift: -20,
  tilt: -1.2,
} as const;

/**
 * `THE`, in block caps — straight tube and four bends, with the E's middle
 * arm held back for `SEG`.
 */
const THE = [
  "M 116 -64 L 328 -64",
  "M 116 55 L 328 55",
  "M 130 -34 L 184 -34",
  "M 157 -34 L 157 28",
  "M 206 -36 L 206 28",
  "M 252 -34 L 252 26",
  "M 206 -4 L 252 -4",
  "M 310 -34 L 282 -34 Q 275 -34 275 -27 L 275 21 Q 275 28 282 28 L 308 28",
] as const;

/**
 * The one length of tube that is not quite right: the E's middle arm.
 *
 * Its own group so the stylesheet can put it on a clock of its own — it drops
 * out briefly and catches again while the rest of the sign holds. A whole sign
 * flickering in unison reads as an animation. One bad tube, in a sign that is
 * otherwise steady, reads as a sign.
 *
 * It is a separate piece of glass in the real object too, which is why it is
 * the one that can fail on its own.
 */
const SEG = ["M 275 -4 L 300 -4"] as const;

// A separate length of bent glass sweeps beneath the script.
const FLOURISH = ["M 365 430 Q 730 353 1165 352"] as const;

/**
 * Mounting hardware: standoff clips holding the tube off the wall.
 *
 * Each one crosses its tube square and overhangs it by a few units either
 * side, which is the whole of what makes it read as a clip rather than as a
 * mark. There were electrode leads here too — short wires trailing away from
 * the ends of the outermost pieces — and they were cut: at this size a dark
 * curve that does not cross anything reads as a hair on the screen, not as
 * hardware.
 *
 * Meant to be found rather than seen. At the opacity `neon.css` gives them
 * they say the sign is fixed to something; at first glance they say nothing.
 *
 * Only on the caps drawn in this file. The script's clips would have to be
 * placed against Sacramento's outlines, and a clip that misses its tube by a
 * few units stops reading as hardware and starts reading as a scratch on the
 * screen.
 */
const CLIPS = [
  // the caps — one per upright
  "M 149 -12 L 165 -12",
  "M 198 14 L 214 14",
  "M 267 10 L 283 10",
] as const;

const draw = (d: readonly string[]) =>
  d.map((path) => <path d={path} key={path} />);

/**
 * The layer stack: one piece of the sign, rendered six times.
 *
 * Bottom to top — three glows at falling radius and rising opacity, the unlit
 * glass, the lit tube, and a hot filament down the centre. The order matters:
 * the glows have to be under the tube or the tube loses its edge, and the
 * unlit glass has to be under the lit tube so that dimming the one reveals the
 * other rather than punching a hole in the sign.
 *
 * `recipe` picks how width is arrived at, not what the layers are, and lands
 * on the element as a second class beside the layer's own. `line` is for
 * centrelines, where the stroke is the whole diameter. `glyph` is for the
 * script, where the letter already has a body, the stroke only swells it, and
 * the hot centre has to be eaten *inwards* out of the body instead — which is
 * what `#neon-erode` is for. A glyph has no centreline to stroke thinly.
 *
 * Each layer is a `<use>` of the same geometry, so the sign is in the document
 * once and the browser draws it six ways. Every width and opacity is set in
 * CSS against `--neon-lit`; nothing here knows how bright the sign is.
 */
function Tubes({ href, recipe }: { href: string; recipe: "line" | "glyph" }) {
  const l = `neon__layer neon__geo--${recipe}`;
  return (
    <>
      <g class={`${l} neon__layer--far`} filter="url(#neon-far)">
        <use href={href} />
      </g>
      <g class={`${l} neon__layer--mid`} filter="url(#neon-mid)">
        <use href={href} />
      </g>
      <g class={`${l} neon__layer--near`} filter="url(#neon-near)">
        <use href={href} />
      </g>
      <use class={`${l} neon__layer--glass`} href={href} />
      <use class={`${l} neon__layer--tube`} href={href} />
      <g
        class={`${l} neon__layer--core`}
        filter={recipe === "glyph" ? "url(#neon-erode)" : "url(#neon-core)"}
      >
        <use href={href} />
      </g>
    </>
  );
}

/**
 * Decorative, and hidden from assistive technology on purpose — the page's
 * `<h1>` carries the words. See `Afterparty`.
 */
export function NeonSign() {
  const region = { x: VIEW.x, y: VIEW.y, width: VIEW.w, height: VIEW.h };

  return (
    <svg
      class="neon__svg"
      viewBox={`${VIEW.x} ${VIEW.y} ${VIEW.w} ${VIEW.h}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {/* Pinned to the canvas rather than to each group's bounding box. A
            percentage region is measured from the box of the thing being
            filtered, and `THE` is small enough that a 26-unit blur ran off the
            end of its own region and drew a bright rectangle around itself.
            One region, the size of the sign, is both correct and one less
            thing to keep in step. */}
        <filter id="neon-far" filterUnits="userSpaceOnUse" {...region}>
          <feGaussianBlur stdDeviation="24" />
        </filter>
        <filter id="neon-mid" filterUnits="userSpaceOnUse" {...region}>
          <feGaussianBlur stdDeviation="10" />
        </filter>
        <filter id="neon-near" filterUnits="userSpaceOnUse" {...region}>
          <feGaussianBlur stdDeviation="3.5" />
        </filter>
        <filter id="neon-core" filterUnits="userSpaceOnUse" {...region}>
          <feGaussianBlur stdDeviation="0.8" />
        </filter>
        {/* The script's hot centre. A filled letter cannot be stroked thinly
            down its middle the way a centreline can, so the body is eaten in
            from both edges instead and what survives is the filament. Erode
            far enough and the hairline joins between letters drop out
            altogether — which is correct, and is what a tube looks like where
            it bends hardest. */}
        <filter id="neon-erode" filterUnits="userSpaceOnUse" {...region}>
          <feMorphology operator="erode" radius="2.6" />
          <feGaussianBlur stdDeviation="0.9" />
        </filter>
        <filter id="neon-cast" filterUnits="userSpaceOnUse" {...region}>
          <feGaussianBlur stdDeviation="7" />
        </filter>

        <g id="neon-the">{draw(THE)}</g>
        <g id="neon-seg">{draw(SEG)}</g>
        <g id="neon-flourish">{draw(FLOURISH)}</g>
        {/* Tracking is left alone on purpose. Sacramento is a joined script —
            the letters meet, and any letter-spacing at all opens the joins and
            turns a word into ten pieces of glass standing near each other. The
            character in this composition comes from scale, tilt and placement
            instead, which is where it should come from anyway. */}
        <g id="neon-script">
          <text
            class="neon__script"
            x={SCRIPT.x}
            y={SCRIPT.y}
            transform={`rotate(${SCRIPT.tilt} ${SCRIPT.x} ${SCRIPT.y})`}
          >
            <tspan font-size={SCRIPT.initialSize}>A</tspan>
            <tspan
              dx={SCRIPT.restOffset}
              dy={SCRIPT.restLift}
              font-size={SCRIPT.restSize}
            >
              fterparty
            </tspan>
          </text>
        </g>
      </defs>

      {/* What the room's own light leaves on the wall. Offset down and to the
          right of the glass, and deliberately not tied to `--neon-lit`: a
          shadow cast by the sign's own tube would fall away from the wall in
          every direction at once, which is not a shadow. */}
      {/* Move the caps as one assembly on mobile: the E's loose arm,
          shadows and hardware must follow the same transform as the glass. */}
      <g class="neon__caps">
        <g class="neon__cast" filter="url(#neon-cast)">
          <use href="#neon-the" />
          <use href="#neon-seg" />
        </g>
        <g class="neon__backing">
          <use href="#neon-the" />
          <use href="#neon-seg" />
        </g>
        <g class="neon__anchors">
          <circle cx="116" cy="-64" r="9" />
          <circle cx="328" cy="55" r="9" />
        </g>
        <g class="neon__mounts">{draw(CLIPS)}</g>
        <g class="neon__the">
          <Tubes href="#neon-the" recipe="line" />
        </g>
        <g class="neon__seg">
          <Tubes href="#neon-seg" recipe="line" />
        </g>
      </g>

      <g class="neon__cast neon__cast--glyph" filter="url(#neon-cast)">
        <use href="#neon-script" />
      </g>
      <g class="neon__backing">
        <use href="#neon-flourish" />
      </g>
      <g class="neon__anchors">
        <circle cx="365" cy="430" r="9" />
        <circle cx="1165" cy="352" r="9" />
      </g>
      <g class="neon__word">
        <Tubes href="#neon-flourish" recipe="line" />
        <Tubes href="#neon-script" recipe="glyph" />
      </g>
    </svg>
  );
}
